import React, { useState } from "react";
import { flushSync } from "react-dom";
import { Button, CircularProgress, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  ContentContainer,
  ModalTitle,
  ModalText,
  ButtonsContainer,
} from "./ApproveConfirmationModal.styled";

interface ApproveConfirmationModalProps {
  onCancel: () => void;
  onApprove: () => Promise<void>;
  onLoadingChange?: (loading: boolean) => void;
}

const ApproveConfirmationModalContent: React.FC<
  ApproveConfirmationModalProps
> = ({ onCancel, onApprove, onLoadingChange }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const handleApprove = async () => {
    // flushSync ensures the loading state renders immediately before the async
    // work starts — without it, React 18 auto-batching can swallow the
    // true→false transition when the operation resolves in a microtask.
    flushSync(() => {
      setIsLoading(true);
    });
    onLoadingChange?.(true);
    try {
      await onApprove();
    } finally {
      setIsLoading(false);
      onLoadingChange?.(false);
    }
  };

  return (
    <ContentContainer>
      <ModalTitle>
        {t("approveConfirmation.title", "Approve Treatment Plan")}
      </ModalTitle>
      <ModalText>
        {t(
          "approveConfirmation.message",
          "Approving this treatment plan will confirm all proposed treatments and make them visible to the care team. You can edit it afterward."
        )}
      </ModalText>
      <ButtonsContainer>
        <Button variant="outlined" onClick={onCancel} disabled={isLoading}>
          {t("common.cancel", "Cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleApprove}
          disabled={isLoading}
          sx={{ minWidth: "160px" }}
        >
          {isLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <CircularProgress size={16} sx={{ flexShrink: 0 }} />
              {t("approveConfirmation.approving", "Approving…")}
            </Box>
          ) : (
            t("approveConfirmation.approveButton", "Approve")
          )}
        </Button>
      </ButtonsContainer>
    </ContentContainer>
  );
};

export default ApproveConfirmationModalContent;
